import { Box } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { INK, STATUS } from "@/theme/tokens";
import MetaChip from "@/ui/Badges/MetaChip";
import { SheetActionRow } from "@/ui/Clickable/SheetActionRow";
import { EMPLOYMENT_TYPE_LABEL_KEY } from "@/utils/utilFunctions";
import { GapsRoleBadge } from "../GapsRoleBadge";
import type { GapsAddCandidate } from "./useGapsAddCandidates";

type Props = {
	candidate: GapsAddCandidate;
	/** The row's place in the list as shown — the analytics `rank`. */
	index: number;
	isSelected: boolean;
	/** The backend's own first place on the ranked, unsearched list — named, never scored. */
	isRankedFirst: boolean;
	/** "Aide 6a-6p" when the person already works a shift that day, else `undefined`. */
	shiftThatDay: string | undefined;
	disabled: boolean;
	/** Stable across renders (the list passes one callback for every row), so an unchanged row skips re-rendering. */
	onPick: (candidate: GapsAddCandidate, index: number) => void;
	dataTestId: string;
};

/**
 * One person the add sheet could add: their role, name, what the ranking was made of (employment type, the overtime
 * the add causes, a shift they already work that day) and the predicted cost.
 *
 * Memoised because the list is the department's whole pool — hundreds of rows once "Show more" or a search opens it —
 * and typing in the search or picking a row re-renders the step: only the rows whose own props changed redraw.
 */
export const GapsAddCandidateRow = memo(function GapsAddCandidateRow({
	candidate,
	index,
	isSelected,
	isRankedFirst,
	shiftThatDay,
	disabled,
	onPick,
	dataTestId,
}: Props) {
	const { t } = useTranslation();
	const overtime = candidate.cost?.overtimeHours;

	return (
		<SheetActionRow
			kind="radio"
			checked={isSelected}
			leading={<GapsRoleBadge role={candidate.nurse.role} size="row" />}
			label={candidate.name}
			description={
				<>
					{t(EMPLOYMENT_TYPE_LABEL_KEY[candidate.nurse.employmentType])}
					{overtime !== undefined && (
						<Box component="span" sx={{ color: overtime > 0 ? STATUS.WARNING.dark : INK.MUTED, fontWeight: overtime > 0 ? 600 : 400 }}>
							{` · ${overtime > 0 ? t("gaps.addSheet.overtime", { hours: Number(overtime.toFixed(1)) }) : t("gaps.addSheet.noOvertime")}`}
						</Box>
					)}
					{shiftThatDay !== undefined && ` · ${t("gaps.addSheet.onShiftThatDay", { shift: shiftThatDay })}`}
				</>
			}
			trailing={
				candidate.cost === null ? undefined : (
					<Box component="span" sx={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 0.375 }}>
						{isRankedFirst && <MetaChip size="small" tone="primary" label={t("gaps.addSheet.rankedFirst")} dataTestId={`${dataTestId}-ranked-first`} />}
						<Box component="span" sx={{ fontWeight: isRankedFirst ? 700 : 500, color: INK.PRIMARY }}>
							{candidate.cost.costDisplay}
						</Box>
					</Box>
				)
			}
			disabled={disabled}
			onClick={() => onPick(candidate, index)}
			dataTestId={dataTestId}
		/>
	);
});
